import { useEffect, useState, useCallback } from 'react'
import { SupportTicketService } from 'utility/services/support-ticket'
import { ErrorConstant } from 'utility'
import { useAuth } from 'context/user'

/**
 * Hook to fetch support tickets by email
 * @param {number|string} [statusFilter] - Optional status filter (1=Open, 2=InProgress, 3=Resolved, 4=Closed, 'all'=all)
 * @returns {Object} { data, loading, error, refresh }
 */
export const useSupportTicketsByEmail = (statusFilter = 'all') => {
    const { user } = useAuth()
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchData = useCallback(async () => {
        if (!user?.Email) {
            setData([])
            return
        }

        try {
            setLoading(true)
            setError('')
            let result

            // If status filter is specified and not 'all', use Get By Status API
            if (statusFilter !== 'all' && statusFilter) {
                result = await SupportTicketService.getByStatus(statusFilter)
                // Filter by email client-side since API filters by status only
                const tickets = Array.isArray(result) ? result : (result.Items || [])
                const filteredTickets = tickets.filter(ticket => ticket.RequesterEmail === user.Email)
                setData(filteredTickets)
            } else {
                // Use Get By Email API for all tickets
                result = await SupportTicketService.getByEmail(user.Email)
                const tickets = Array.isArray(result) ? result : (result.Items || [])
                setData(tickets)
            }
        } catch (e) {
            setError(ErrorConstant.default || 'Failed to fetch tickets')
            setData([])
        } finally {
            setLoading(false)
        }
    }, [user?.Email, statusFilter])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    return { data, loading, error, refresh: fetchData }
}

/**
 * Hook to fetch support ticket by ID
 * @param {number} ticketId - Ticket ID
 * @returns {Object} { data, loading, error, refresh }
 */
export const useSupportTicketById = (ticketId) => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchData = async () => {
        if (!ticketId) {
            setData(null)
            return
        }

        try {
            setLoading(true)
            setError('')
            const result = await SupportTicketService.getById(ticketId)
            setData(result)
        } catch (e) {
            setError(ErrorConstant.default || 'Failed to fetch ticket')
            setData(null)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [ticketId])

    return { data, loading, error, refresh: fetchData }
}
